import {
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import * as _ from 'lodash';

import { ColorOptionConverter, FormatOptionConverter, OptionGenerator } from '@selfai-platform/bi-chart-engine';
import {
  CellColorTarget,
  ChartColorList,
  ChartColorType,
  ChartType,
  ColorCustomMode,
  ColorRange,
  ColorRangeType,
  DashboardField as Field,
  DatasourceField,
  GridViewType,
  Pivot,
  ShelveFieldType,
  UIChartColor,
  UIChartColorByDimension,
  UIChartColorByMeasure,
  UIChartColorBySeries,
  UIChartColorByValue,
  UIGridChart,
  UIOption,
} from '@selfai-platform/bi-domain';

import { ColorPickerComponent } from '../../../common/component/color-picker/color.picker.component';
import { GradationGeneratorComponent } from '../../../common/component/gradation/gradation-generator.component';
import { RangeSliderComponent } from '../../component/analysis/slider/range-slider.component';
import { BaseOptionComponent } from '../base-option.component';
import UI = OptionGenerator.UI;

const colorTypeList: Object[] = [
  { label: 'None', id: ChartColorType.SINGLE },
  { label: 'Series', id: ChartColorType.SERIES },
  { label: 'Dimension', id: ChartColorType.DIMENSION },
  { label: 'Measure', id: ChartColorType.MEASURE },
];

@Component({
    selector: 'color-option',
    templateUrl: './color-option.component.html',
    styles: ['.sys-inverted {transform: scaleX(-1);}'],
    standalone: false
})
export class ColorOptionComponent extends BaseOptionComponent implements OnInit, OnDestroy {
  @ViewChildren('colorChartSlider')
  public colorRangeComp: QueryList<RangeSliderComponent>;

  @ViewChild('colorPicker')
  public colorPicker: ColorPickerComponent;

  @ViewChild(GradationGeneratorComponent)
  public gradationComp: GradationGeneratorComponent;

  @Input() public pivot: Pivot;

  @Input('resultData')
  public set setResultData(resultData: Object) {
    this.resultData = resultData;
    if (resultData?.['data']?.['info'] && this.uiOption) {
      const tmpInfo = resultData['data']['info'];
      const tmpValFormat = this.uiOption.valueFormat;
      const minValue = this.checkMinZero(tmpInfo['minValue'], tmpInfo['minValue']);
      this.minValue = FormatOptionConverter.getDecimalValue(
        minValue,
        tmpValFormat.decimal,
        tmpValFormat.useThousandsSep,
      );
      this.maxValue = FormatOptionConverter.getDecimalValue(
        tmpInfo['maxValue'],
        tmpValFormat.decimal,
        tmpValFormat.useThousandsSep,
      );
    }
  }

  @Input('uiOption')
  public set setUiOption(uiOption: UIOption) {
    this.uiOption = uiOption;

    if (!this.uiOption.fieldList || 0 == this.uiOption.fieldList.length) {
      this.uiOption.fieldList = this.setFieldList(ShelveFieldType.DIMENSION);
    }

    if (this.color.type === ChartColorType.MEASURE) {
      this.uiOption.fieldList = this.setFieldList(ShelveFieldType.MEASURE);
    }

    if (!this.color['ranges'] && ChartColorType.MEASURE == this.color.type) {
      const colorList = <any>ChartColorList[this.color['schema']];

      this.color['ranges'] = ColorOptionConverter.setMeasureColorRange(
        this.uiOption,
        this.resultData['data'],
        colorList,
      );

      this.resultData['type'] = null;
    } else if (
      this.color['customMode'] &&
      ChartColorType.MEASURE == this.color.type &&
      ColorCustomMode.GRADIENT == this.color['customMode']
    ) {
      this.changeDetect.detectChanges();

      if (!this.gradationComp.gradxObj) {
        const obj = this.gradationInit(this.color['ranges'], true);

        if (obj) {
          this.color['ranges'] = obj['ranges'];

          this.rangesViewList = this.color['ranges'];
          this.color['visualGradations'] = obj['visualGradations'];
        }
      }
    }

    setTimeout(() => {
      this.rangesViewList = this.setRangeViewByDecimal(this.color['ranges']);
    }, 100);

    if (this.uiOption.valueFormat && undefined !== this.uiOption.valueFormat.decimal) {
      const minValue = this.checkMinZero(this.uiOption.minValue, this.uiOption.minValue);

      this.minValue = FormatOptionConverter.getDecimalValue(
        minValue,
        this.uiOption.valueFormat.decimal,
        this.uiOption.valueFormat.useThousandsSep,
      );
      this.maxValue = FormatOptionConverter.getDecimalValue(
        this.uiOption.maxValue,
        this.uiOption.valueFormat.decimal,
        this.uiOption.valueFormat.useThousandsSep,
      );
    }

    this.$colorPickerPopup = $('#colorPanelColorPicker');
  }

  get color(): UIChartColor {
    return this.uiOption.color || ({} as UIChartColor);
  }

  public measureText: string;
  public defaultColorList: Object[] = [
    { index: 1, colorNum: 'SC1' },
    { index: 2, colorNum: 'SC2' },
    { index: 3, colorNum: 'SC3' },
    { index: 4, colorNum: 'SC4' },
    { index: 5, colorNum: 'SC5' },
    { index: 6, colorNum: 'SC6' },
    { index: 7, colorNum: 'SC7' },
    { index: 8, colorNum: 'SC8' },
    { index: 9, colorNum: 'SC9' },
  ];
  public measureColorList: Object[] = [
    { index: 1, colorNum: 'VC1' },
    { index: 2, colorNum: 'VC2' },
    { index: 3, colorNum: 'VC3' },
    { index: 4, colorNum: 'VC4' },
    { index: 5, colorNum: 'VC5' },
    { index: 6, colorNum: 'VC6' },
    { index: 7, colorNum: 'VC7' },
  ];
  public measureReverseColorList: Object[] = [
    { index: 8, colorNum: 'VC8' },
    { index: 9, colorNum: 'VC9' },
    { index: 10, colorNum: 'VC10' },
    { index: 11, colorNum: 'VC11' },
    { index: 12, colorNum: 'VC12' },
    { index: 13, colorNum: 'VC13' },
    { index: 14, colorNum: 'VC14' },
    { index: 15, colorNum: 'VC15' },
    { index: 16, colorNum: 'VC16' },
    { index: 17, colorNum: 'VC17' },
    { index: 18, colorNum: 'VC18' },
    { index: 19, colorNum: 'VC19' },
    { index: 20, colorNum: 'VC20' },
    { index: 21, colorNum: 'VC21' },
    { index: 22, colorNum: 'VC22' },
    { index: 23, colorNum: 'VC23' },
    { index: 24, colorNum: 'VC24' },
    { index: 25, colorNum: 'VC25' },
    { index: 26, colorNum: 'VC26' },
    { index: 27, colorNum: 'VC27' },
  ];
  public selectedDefaultColor: Object = this.defaultColorList[0];
  public selectedMeasureColor: Object = this.measureColorList[0];
  public colorListFlag = false;
  public colorTypeFlag = false;
  public currentRange: ColorRange;
  public colorRangeTypeList = [
    {
      name: this.translateService.instant('msg.page.chart.color.measure.new.range.type.default'),
      value: ColorCustomMode.SECTION,
    },
    {
      name: this.translateService.instant('msg.page.chart.color.measure.new.range.type.gradient'),
      value: ColorCustomMode.GRADIENT,
    },
  ];
  public minValue: string;
  public maxValue: string;
  public availableRangeValue: string;
  public separateValue = 10;
  public rangesViewList = [];
  public resultData: Object;
  public isTemplateColorInverted: boolean = undefined;

  public isMeasureColorsMode: boolean = false;

  private gradationIndex: number;
  private $colorPickerPopup: JQuery;

  constructor(protected elementRef: ElementRef, protected injector: Injector, private zone: NgZone) {
    super(elementRef, injector);
  }

  public ngOnInit() {
    super.ngOnInit();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public toggleColorList() {
    this.colorListFlag = !this.colorListFlag;
  }

  private setMeasureColorForFieldByIndex(schema: string, index: number): void {
    this.color['schema'] = schema;
    this.colorByMeasure(this.uiOption.fieldMeasureList[index]['field']);
    this.changeColor(this.measureReverseColorList[index]);
  }

  public changeColorType(typeId: string) {
    if (typeId)
    (<UIChartColorBySeries>this.color).settingUseFl = false;

    const selectedColorType = _.find(colorTypeList, (item) => {
      return item['id'] === typeId;
    });

    let schema;

    if (JSON.stringify(this.color.type?.['id']) !== JSON.stringify(selectedColorType['id'])) {
      schema =
        ChartColorType.MEASURE === selectedColorType['id']
          ? this.measureColorList[0]['colorNum']
          : this.defaultColorList[0]['colorNum'];

      if (ChartColorType.MEASURE.toString() == typeId) {
        this.color['ranges'] = ColorOptionConverter.setMeasureColorRange(
          this.uiOption,
          this.resultData['data'],
          <any>ChartColorList[schema],
        );
        this.setMeasureColorForFieldByIndex(schema, 0);
      } else if (ChartColorType.SERIES.toString() == typeId) {
        this.uiOption.color = this.setMapping();
      }

      if (_.eq(this.uiOption.type, ChartType.GRID)) {
        schema = ChartColorType.MEASURE === selectedColorType['id'] ? this.measureReverseColorList[0]['colorNum'] : [];

        if (_.isUndefined(this.color['colorTarget'])) {
          this.color['colorTarget'] = CellColorTarget.TEXT;
        }
      }
    } else {
      schema = (<UIChartColorByDimension>this.color).schema;
    }

    const colorOption = {
      type: selectedColorType['id'],
      showFl: this.color['showFl'],
    };

    if ('single' == typeId) {
      colorOption['code'] = '';
    } else {
      colorOption['schema'] = schema;
      colorOption['colorTarget'] = this.color['colorTarget'];

      if ('series' == typeId) {
        colorOption['mapping'] = this.color['mapping'];
        colorOption['mappingArray'] = this.color['mappingArray'];
      } else if ('measure' == typeId) {
        colorOption['ranges'] = this.color['ranges'];
      }
    }

    if (typeId == ChartColorType.DIMENSION.toString() || typeId == ChartColorType.MEASURE.toString()) {
      colorOption['targetField'] = _.last(this.uiOption.fieldList);

      if (ChartType.GAUGE == this.uiOption.type) {
        this.uiOption.legend.auto = false;
      }
    }

    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: colorOption,
      legend: this.uiOption.legend,
    });

    this.isMeasureColorsMode = typeId == ChartColorType.MEASURE.toString()

    this.update();
  }

  public colorByDimension(field: Field): void {
    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: {
        type: ChartColorType.DIMENSION,
        schema: (<UIChartColorByDimension>this.color).schema,
        targetField: !field ? _.last(this.uiOption.fieldList) : field.name,
        showFl: this.color['showFl'],
      },
    });

    this.update();
  }

  public colorByMeasure(field: Field): void {
    this.color['targetField'] = field.name;
    let measureColorField = (<UIGridChart>this.uiOption)?.measureColors?.find(
      (color) => color['targetField'] === field.name
    );

    if (measureColorField) {
      (<UIChartColorByMeasure>this.color).schema = measureColorField['schema'];

      return;
    }
    const newMeasureColorField = {
      type: ChartColorType.MEASURE,
      schema: (<UIChartColorByMeasure>this.color).schema,
      targetField: field.name,
      showFl: this.color['showFl'],
    };
    if ((<UIGridChart>this.uiOption)?.measureColors?.length) {
      (<UIGridChart>this.uiOption).measureColors.push(newMeasureColorField);
    } else {
      (<UIGridChart>this.uiOption).measureColors = [newMeasureColorField];
    }
    this.isMeasureColorsMode = true;

    this.update();
  }

  public changeColor(colorObj: Object, gridColorObj?: Object) {
    let color = _.cloneDeep(colorObj);
    if ($('input#invertColor').is(':checked')) {
      color['colorNum'] = 'R' + color['colorNum'];
    }

    if (ChartColorType.MEASURE === this.color.type) {
      this.color['ranges'] = ColorOptionConverter.setMeasureColorRange(
        this.uiOption,
        this.resultData['data'],
        ChartColorList[color['colorNum']],
      );

      if (_.eq(this.uiOption.type, ChartType.GRID)) {
        this.selectedMeasureColor = color;
        const gridColor = _.cloneDeep(gridColorObj);
        if ($('input#invertColor').is(':checked')) {
          gridColor['colorNum'] = 'R' + gridColor['colorNum'];
        }
        color = gridColor;
      }
    } else {
      this.selectedDefaultColor = color;
    }

    this.setUserCodes(color);

    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: {
        type: this.color.type,
        schema: color['colorNum'],
        ranges: this.color['ranges'],
        targetField: this.color['targetField'],
        colorTarget: this.color['colorTarget'],
        settingUseFl: (<UIChartColorBySeries>this.color).settingUseFl,
        showFl: this.color['showFl'],
        mapping: (<UIChartColorBySeries>this.color).mapping,
        mappingArray: (<UIChartColorBySeries>this.color).mappingArray,
      },
    });

    let measureColorToUpdate = (<UIGridChart>this.uiOption).measureColors
      ?.find((c) => c['targetField'] === this.color['targetField']);
    measureColorToUpdate['type'] = this.color['type'];
    measureColorToUpdate['schema'] = this.color['schema'];
    measureColorToUpdate['ranges'] = this.color['ranges'];
    const resultDataCopy = _.cloneDeep(this.resultData['data']);
    const uiOptionCopy = _.cloneDeep(this.uiOption);
    const fieldAlias = this.resultData['config']['pivot']['aggregations']?.find((i) => i?.name === this.color['targetField'])?.alias;

    if (uiOptionCopy.dataType === GridViewType.PIVOT) {
      resultDataCopy.info.minValue = uiOptionCopy.minValue = resultDataCopy.info?.measuresMinMax[fieldAlias].minValue;
      resultDataCopy.info.maxValue = uiOptionCopy.maxValue =  resultDataCopy.info?.measuresMinMax[fieldAlias].maxValue;
    } else if (uiOptionCopy.dataType === GridViewType.MASTER) {
      resultDataCopy.info.minValue = uiOptionCopy.minValue = resultDataCopy.info?.minValue;
      resultDataCopy.info.maxValue = uiOptionCopy.maxValue =  resultDataCopy.info?.maxValue;
    }
    measureColorToUpdate['ranges'] = ColorOptionConverter.setMeasureColorRange(
      uiOptionCopy,
      resultDataCopy,
      ChartColorList[color['colorNum']],
    );
    measureColorToUpdate['targetField'] = this.color['targetField'];
    measureColorToUpdate['colorTarget'] = this.color['colorTarget'];
    measureColorToUpdate['settingUseFl'] = this.color['settingUseFl'];
    measureColorToUpdate['mapping'] = this.color['mapping'];
    measureColorToUpdate['mappingArray'] = this.color['mappingArray'];

    this.update();
  }

  public invertColor() {
    event.stopPropagation();

    if ($('input#invertColor').is(':checked')) {
      this.isTemplateColorInverted = true;
    } else {
      this.isTemplateColorInverted = false;
    }

    let colorList: Object[] = [];

    colorList = colorList.concat(this.measureColorList);
    colorList = colorList.concat(this.measureReverseColorList);

    for (const item of colorList) {
      if (this.color['schema'].endsWith(item['colorNum'])) {
        this.changeColor(item, item);
      }
    }
  }

  public isChartColorInverted() {
    return this.color['schema'].indexOf('R') === 0;
  }

  public isChartColorSelected(item) {
    return this.color['schema'].endsWith(item['colorNum']);
  }

  public checkDefaultSelectedColor(): number {
    for (const item of this.defaultColorList) {
      if (JSON.stringify(this.color['schema']) === JSON.stringify(item['colorNum'])) {
        return item['index'];
      }
    }
  }

  public checkMeasureSelectedColor(): any {
    let colorList: Object[] = [];

    colorList = colorList.concat(this.measureColorList);
    colorList = colorList.concat(this.measureReverseColorList);

    for (const item of colorList) {
      if (this.color['schema'].endsWith(item['colorNum'])) {
        return item['index'];
      }
    }
  }

  public lineChartCheck(chartType: string): boolean {
    return true;
  }

  public showUserColorSet() {
    const colorObj: UIChartColorBySeries = <UIChartColorBySeries>this.color;

    colorObj.settingUseFl = !colorObj.settingUseFl;
  }

  public resetUserColorSet(item: any, index: number) {
    event.stopPropagation();

    const colorList = ChartColorList[(<UIChartColorBySeries>this.color).schema];

    (<UIChartColorBySeries>this.color).mapping[item.alias] = colorList[index];
    (<UIChartColorBySeries>this.color).mappingArray[index]['color'] = colorList[index];

    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: {
        type: this.color.type,
        schema: (<UIChartColorBySeries>this.color).schema,
        mapping: (<UIChartColorBySeries>this.color).mapping,
        mappingArray: (<UIChartColorBySeries>this.color).mappingArray,
        settingUseFl: (<UIChartColorBySeries>this.color).settingUseFl,
      },
    });

    this.update();
  }

  colorPaletteInput(event: string, item: DatasourceField): void {
    if (typeof event !== 'string') {
      return;
    }
    let value = event.trim();

    if (value.startsWith('#')) {
      value = value.substring(1);
    }

    if (value.length === 3 || value.length === 6) {
      this.colorPaletteSelected(`#${value}`, item);
    }
  }

  public colorPaletteSelected(colorCode: string, item?: any) {
    if (this.color.type == ChartColorType.SERIES) {
      const index = _.findIndex(this.uiOption.fieldMeasureList, { alias: item.alias });

      const color = ChartColorList[(<UIChartColorBySeries>this.color).schema];

      if (index !== -1) {
        if (!(<UIChartColorBySeries>this.color).mapping) {
          (<UIChartColorBySeries>this.color).mapping = _.cloneDeep(color);
        }

        (<UIChartColorBySeries>this.color).mappingArray[index]['color'] = colorCode;

        (<UIChartColorBySeries>this.color).mapping[(<UIChartColorBySeries>this.color).mappingArray[index]['alias']] =
          colorCode;

        if (_.eq(this.uiOption.type, ChartType.GRID)) {
          if (_.isUndefined(this.color['colorTarget'])) {
            this.color['colorTarget'] = CellColorTarget.TEXT;
          }
        }

        this.uiOption = <UIOption>_.extend({}, this.uiOption, {
          color: {
            type: this.color.type,
            mapping: (<UIChartColorBySeries>this.color).mapping,
            mappingArray: (<UIChartColorBySeries>this.color).mappingArray,
            schema: (<UIChartColorBySeries>this.color).schema,
            settingUseFl: (<UIChartColorBySeries>this.color).settingUseFl,
            colorTarget: this.color['colorTarget'],
          },
        });

        this.update();
      }
    } else if (this.color.type == ChartColorType.MEASURE) {
      const index = this.rangesViewList.findIndex((rangeItem) => rangeItem.color === item.color);

      (<UIChartColorByValue>this.color).ranges[index].color = colorCode;

      if (_.eq(this.uiOption.type, ChartType.GRID)) {
        if (_.isUndefined(this.color['colorTarget'])) {
          this.color['colorTarget'] = CellColorTarget.TEXT;
        }
      }

      this.uiOption = <UIOption>_.extend({}, this.uiOption, {
        color: {
          type: this.color.type,
          schema: (<UIChartColorBySeries>this.color).schema,
          ranges: (<UIChartColorByValue>this.color).ranges,
          customMode: (<UIChartColorByValue>this.color).customMode,
          colorTarget: this.color['colorTarget'],
        },
      });

      this.currentRange = null;

      this.update();
    }
  }

  public addNewRange(index: number) {
    this.removeInputRangeStatus();

    const rangeList = (<UIChartColorByValue>this.color).ranges;

    const uiMinValue =
      this.uiOption.minValue >= 0
        ? 0
        : Math.floor(Number(this.uiOption.minValue) * Math.pow(10, this.uiOption.valueFormat.decimal)) /
          Math.pow(10, this.uiOption.valueFormat.decimal);

    const maxValue = rangeList[index - 1].gt;
    let minValue = rangeList[index].gt ? rangeList[index].gt : uiMinValue;

    minValue = minValue + (maxValue - minValue) / 2;

    const formatMinValue =
      Math.floor(Number(minValue) * Math.pow(10, this.uiOption.valueFormat.decimal)) /
      Math.pow(10, this.uiOption.valueFormat.decimal);
    const formatMaxValue =
      Math.floor(Number(maxValue) * Math.pow(10, this.uiOption.valueFormat.decimal)) /
      Math.pow(10, this.uiOption.valueFormat.decimal);

    rangeList[index].lte = formatMinValue;
    rangeList[index].fixMax = formatMinValue;

    const currentColor = rangeList[index].color;

    rangeList.splice(
      index,
      0,
      UI.Range.colorRange(
        ColorRangeType.SECTION,
        currentColor,
        formatMinValue,
        formatMaxValue,
        formatMinValue,
        formatMaxValue,
      ),
    );

    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: {
        type: this.color.type,
        schema: (<UIChartColorByValue>this.color).schema,
        ranges: rangeList,
        customMode: (<UIChartColorByValue>this.color).customMode,
        colorTarget: this.color['colorTarget'],
      },
    });

    this.update();
  }

  public changeColorRange() {
    let colorOption = <any>this.color;

    if (!this.color['customMode']) {
      colorOption['customMode'] = ColorCustomMode.SECTION;

      if (!(<UIChartColorByValue>this.color).ranges) {
        const ranges = ColorOptionConverter.setMeasureColorRange(
          this.uiOption,
          this.resultData['data'],
          ChartColorList[this.color['schema']],
        );

        colorOption = {
          type: this.color.type,
          schema: (<UIChartColorBySeries>this.color).schema,
          customMode: (<UIChartColorByValue>this.color).customMode,
          ranges: ranges,
        };
      }
    } else {
      const ranges = ColorOptionConverter.setMeasureColorRange(
        this.uiOption,
        this.resultData['data'],
        <any>ChartColorList[(<UIChartColorBySeries>this.color).schema],
      );

      colorOption = {
        type: this.color.type,
        schema: (<UIChartColorBySeries>this.color).schema,
        ranges: ranges,
      };
    }

    if (_.eq(this.uiOption.type, ChartType.GRID)) {
      if (_.isUndefined(this.color['colorTarget'])) {
        colorOption['colorTarget'] = CellColorTarget.TEXT;
      } else {
        colorOption['colorTarget'] = this.color['colorTarget'];
      }
    }

    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: colorOption,
    });

    this.update();
  }

  public removeColorRange(range: ColorRange, index: number) {
    const rangeList = (<UIChartColorByValue>this.color).ranges;

    if (1 == rangeList.length) return;

    const upperValue = rangeList[index - 1] ? rangeList[index - 1] : null;
    const lowerValue = rangeList[index + 1] ? rangeList[index + 1] : null;

    if (upperValue && lowerValue) {
      const upperMaxValue = rangeList[index - 1].lte ? rangeList[index - 1].lte : rangeList[index - 1].gt;

      const lowerMinValue = rangeList[index + 1].gt ? rangeList[index + 1].gt : rangeList[index + 1].lte;

      const autoChangeValue =
        Math.floor(Number((upperMaxValue + lowerMinValue) / 2) * Math.pow(10, this.uiOption.valueFormat.decimal)) /
        Math.pow(10, this.uiOption.valueFormat.decimal);

      rangeList[index - 1].gt = autoChangeValue;
      rangeList[index - 1].fixMin = autoChangeValue;

      rangeList[index + 1].lte = autoChangeValue;
      rangeList[index + 1].fixMax = autoChangeValue;
    }

    rangeList.splice(index, 1);

    this.uiOption = <UIOption>_.extend({}, this.uiOption, { color: this.color });

    this.update();
  }

  public selectColorRangeType(customMode: Object): void {
    if (this.color['customMode'] == customMode['value']) return;

    this.color['customMode'] = customMode['value'];

    if (ColorCustomMode.GRADIENT == customMode['value']) {
      delete this.color['ranges'];
      delete this.color['visualGradations'];

      const obj = this.gradationInit(this.color['ranges'], true);

      this.color['ranges'] = obj['ranges'];
      this.rangesViewList = this.color['ranges'];
      this.color['visualGradations'] = obj['visualGradations'];
    } else if (ColorCustomMode.SECTION == customMode['value']) {
      delete this.color['ranges'];
      delete this.color['visualGradations'];

      this.color['ranges'] = ColorOptionConverter.setMeasureColorRange(
        this.uiOption,
        this.resultData['data'],
        <any>ChartColorList[this.color['schema']],
      );
    }

    this.uiOption = <UIOption>_.extend({}, this.uiOption, { color: this.color });

    this.update();
  }

  private gradationInit(gradations: Object[], initFl?: boolean): Object {
    const colorList = ChartColorList[this.color['schema']];

    const minValue = this.checkMinZero(this.uiOption.minValue, parseInt(this.uiOption.minValue.toFixed(0)));
    const maxValue = parseInt(this.uiOption.maxValue.toFixed(0));

    if (!gradations || gradations.length == 0) {
      gradations = [
        {
          color: colorList[0],
          position: -4,
          value: minValue,
        },
        {
          color: colorList[colorList.length - 1],
          position: 215,
          value: maxValue,
        },
      ];
    }

    const data: Object = {
      min: minValue,
      max: maxValue,
      separateValue: this.separateValue,
      positionMin: -4,
      positionMax: 215,
    };

    this.changeDetect.detectChanges();

    const obj = this.gradationComp.init(gradations, data, initFl);

    if (initFl)
      obj['ranges'].forEach((item) => {
        if (!item['type']) {
          item['type'] = ColorRangeType.GRADIENT;
          return item;
        } else {
          return item;
        }
      });

    return obj;
  }

  public changeRangeMinInput(range: any, index: number): void {
    const isNumber = range.gt;

    const rangeList = (<UIChartColorByValue>this.color).ranges;

    if (!range.gt || isNaN(FormatOptionConverter.getNumberValue(range.gt)) || isNumber == false) {
      range.gt = _.cloneDeep(
        FormatOptionConverter.getDecimalValue(
          rangeList[index].fixMin,
          this.uiOption.valueFormat.decimal,
          this.uiOption.valueFormat.useThousandsSep,
        ),
      );
      return;
    }

    range = this.parseStrFloat(range);

    const decimalValue = this.uiOption.minValue;

    const uiMinValue = this.checkMinZero(this.uiOption.minValue, decimalValue);

    const minValue = rangeList[index + 1]
      ? rangeList[index + 1].gt
        ? rangeList[index + 1].gt
        : uiMinValue
      : rangeList[index].gt
      ? rangeList[index].gt
      : rangeList[index].lte;
    const maxValue = range.lte;

    if (!rangeList[index - 1]) {
      if (this.uiOption.maxValue < range.gt || rangeList[index + 1].fixMax > range.gt) {
        range.gt = range.fixMin;
      } else {
        range.fixMin = range.gt;
      }
    } else if (minValue > range.gt || maxValue < range.gt) {
      range.gt = range.fixMin;
    } else {
      range.fixMin = range.gt;
    }

    if (rangeList[index + 1]) {
      rangeList[index + 1].lte = range.gt;
      rangeList[index + 1].fixMax = range.gt;
    }

    rangeList[index] = range;

    this.uiOption = <UIOption>_.extend({}, this.uiOption, { color: this.color });

    this.update();
  }

  public changeRangeMaxInput(range: any, index: number): void {
    const isNumber = range.lte;

    const rangeList = (<UIChartColorByValue>this.color).ranges;

    if (!range.lte || isNaN(FormatOptionConverter.getNumberValue(range.lte)) || isNumber == false) {
      range.lte = _.cloneDeep(
        FormatOptionConverter.getDecimalValue(
          rangeList[index].fixMax,
          this.uiOption.valueFormat.decimal,
          this.uiOption.valueFormat.useThousandsSep,
        ),
      );
      return;
    }

    range = this.parseStrFloat(range);

    const uiMinValue = this.checkMinZero(this.uiOption.minValue, this.uiOption.minValue);

    const lowerfixMin = rangeList[index + 1]
      ? rangeList[index + 1].fixMin
        ? rangeList[index + 1].fixMin
        : rangeList[index + 1].fixMax
      : null;

    if (!rangeList[index + 1]) {
      if (uiMinValue < range.lte && rangeList[index - 1].fixMin > range.lte) {
        range.fixMax = range.lte;
        rangeList[index - 1].gt = range.lte;
      } else {
        range.lte = range.fixMax;
      }
    } else if (range.fixMax < range.lte || lowerfixMin > range.lte) {
      range.lte = range.fixMax;
    } else {
      range.fixMax = range.lte;
    }

    if (rangeList[index - 1]) {
      rangeList[index - 1].fixMin = range.lte;
      rangeList[index - 1].gt = range.lte;
    }

    if (null != range.fixMin && rangeList[index + 1] && range.fixMin > range.fixMax) {
      range.gt = range.fixMax;
      rangeList[index + 1].lte = range.fixMax;
      rangeList[index + 1].fixMax = range.fixMax;
    }

    rangeList[index] = range;

    this.uiOption = <UIOption>_.extend({}, this.uiOption, { color: this.color });

    this.update();
  }

  public equalColorRange(): void {
    const rangeList = (<UIChartColorByValue>this.color).ranges;

    const colorList = <any>_.cloneDeep(ChartColorList[this.color['schema']]);

    rangeList.reverse().forEach((item, index) => {
      colorList[index] = item.color;
    });

    this.color['ranges'] = ColorOptionConverter.setMeasureColorRange(
      this.uiOption,
      this.resultData['data'],
      colorList,
      rangeList,
    );

    this.uiOption = <UIOption>_.extend({}, this.uiOption, { color: this.color });

    this.update();
  }

  public availableRange(currentRnage: any, index: number): void {
    const rangeList = this.rangesViewList;

    let returnString = '';

    if (0 == index) {
      returnString += ': ' + currentRnage.fixMin;
    } else if (rangeList.length - 1 == index) {
      returnString += ': ' + currentRnage.fixMax;
    } else {
      const availableMin = !rangeList[index + 1]
        ? null
        : rangeList[index + 1].fixMin
        ? rangeList[index + 1].fixMin
        : rangeList[index + 1].fixMax;
      const availableMax = currentRnage.fixMax;

      if (null !== availableMin) returnString += ': ' + availableMin.toString() + ' ~ ';
      if (null !== availableMax) returnString += availableMax.toString();
    }

    this.availableRangeValue = returnString;
  }

  public colorGaugePaletteSelected(colorCode: string, item?: any) {
    const index = _.findIndex(this.uiOption.fieldDimensionDataList, (data) => {
      return item.alias == data;
    });

    const color = ChartColorList[(<UIChartColorByDimension>this.color).schema];

    if (-1 !== index) {
      if (!(<UIChartColorByDimension>this.color).mapping) {
        (<UIChartColorByDimension>this.color).mapping = _.cloneDeep(color);
      }

      (<UIChartColorByDimension>this.color).mappingArray[index]['color'] = colorCode;

      (<UIChartColorByDimension>this.color).mapping[
        (<UIChartColorByDimension>this.color).mappingArray[index]['alias']
      ] = colorCode;

      this.uiOption = <UIOption>_.extend({}, this.uiOption, {
        color: {
          type: this.color.type,
          mapping: (<UIChartColorByDimension>this.color).mapping,
          mappingArray: (<UIChartColorByDimension>this.color).mappingArray,
          schema: (<UIChartColorByDimension>this.color).schema,
          colorTarget: this.color['colorTarget'],
          settingUseFl: (<UIChartColorBySeries>this.color).settingUseFl,
        },
      });

      this.update();
    }
  }

  public resetGaugeUserColorSet(item: any, index: number) {
    event.stopPropagation();

    const colorList = ChartColorList[(<UIChartColorByDimension>this.color).schema];

    (<UIChartColorByDimension>this.color).mapping[item.alias] = colorList[index];
    (<UIChartColorByDimension>this.color).mappingArray[index]['color'] = colorList[index];

    this.uiOption = <UIOption>_.extend({}, this.uiOption, {
      color: {
        type: this.color.type,
        schema: (<UIChartColorByDimension>this.color).schema,
        mapping: (<UIChartColorByDimension>this.color).mapping,
        mappingArray: (<UIChartColorByDimension>this.color).mappingArray,
        settingUseFl: (<UIChartColorByDimension>this.color).settingUseFl,
      },
    });

    this.update();
  }

  public changeGradations(data: Object) {
    this.color['visualGradations'] = data['visualGradations'];

    data['ranges'].forEach((item) => {
      if (!item.type) {
        item.type = ColorRangeType.GRADIENT;
        return item;
      } else {
        return item;
      }
    });

    if (JSON.stringify(data['ranges']) != JSON.stringify(this.color['ranges'])) {
      this.color['ranges'] = data['ranges'];

      this.rangesViewList = this.color['ranges'];
    }

    this.zone.run(() => {
      this.uiOption = <UIOption>_.extend({}, this.uiOption, {
        color: this.color,
      });
    });

    this.changeDetect.detectChanges();

    this.$element.find('.ddp-box-color').find('.sp-replacer').removeClass('sp-active');

    this.$element
      .find('.ddp-box-color#ddp-box-color-' + data['currentSliderIndex'])
      .find('.sp-replacer')
      .addClass('sp-active');

    this.update();
  }

  public addGradientRange(currentIndex: number) {
    currentIndex = 0 !== currentIndex ? currentIndex - 1 : 0;

    this.gradationComp.addNewRangeIndex(currentIndex);
  }

  public deleteGradientRange(currentIndex: number) {
    this.$element.find('.ddp-box-color').find('.sp-replacer').removeClass('sp-active');

    this.gradationComp.unselectSlider();

    this.gradationComp.deleteRange(currentIndex);
  }

  public gradationColorSelected(colorStr: string, item: any) {
    const rgbColor = this.setHextoRgb(colorStr);

    this.gradationComp.changeGradationColor(item.index, rgbColor);
  }

  public showGradationColor(item, index) {
    if (undefined == this.gradationIndex && undefined !== item.value) {
      let top = $('.sp-container').not('.sp-hidden').offset().top;
      top += 30;
      $('.sp-container').not('.sp-hidden').css({ top: top });
    }

    this.gradationIndex = index;

    this.$element
      .find('.ddp-box-color')
      .not('#ddp-box-color-' + index)
      .find('.sp-replacer')
      .removeClass('sp-active');

    this.gradationComp.unselectSlider(item.index);
  }

  public hideGradationColor(index?: number) {
    if (!$(event.target).attr('class')) return;

    if (-1 !== $(event.target).attr('class').indexOf('gradx_slider')) {
      this.gradationIndex = undefined;
    } else if (
      -1 == $(event.target).attr('class').indexOf('ddp-input-txt') &&
      -1 == $(event.target).attr('class').indexOf('sp-preview-inner') &&
      -1 == $(event.target).attr('class').indexOf('ddp-icon-apply') &&
      -1 == $(event.target).attr('class').indexOf('ddp-list-blank')
    ) {
      this.$element.find('.ddp-box-color').find('.sp-replacer').removeClass('sp-active');

      this.changeDetect.detectChanges();

      this.gradationComp.unselectSlider();

      this.gradationIndex = undefined;
    } else if (null !== index && -1 !== $(event.target).attr('class').indexOf('ddp-input-txt')) {
      this.$element.find('#ddp-box-color-' + index + ' .sp-replacer').addClass('sp-active');
    }
  }

  public returnColorType(type: string) {
    switch (type) {
      case ChartColorType.SINGLE.toString():
        return this.translateService.instant('msg.space.ui.none');
      case ChartColorType.SERIES.toString():
        return this.translateService.instant('msg.page.li.color.series');
      case ChartColorType.DIMENSION.toString():
        return this.translateService.instant('msg.page.li.color.dimension');
      case ChartColorType.MEASURE.toString():
        return this.translateService.instant('msg.page.li.color.measure');
    }
  }

  public getDimensionIndex() {
    if (!this.uiOption.fielDimensionList) return;

    const index = _.findIndex(this.uiOption.fielDimensionList, { name: this.color['targetField'] });

    return index;
  }

  public getMeasureIndex() {
    if (!this.uiOption.fieldMeasureList) return;

    return _.findIndex(this.uiOption.fieldMeasureList, { name: this.color['targetField'] });
  }

  public showMinInputColorRange(item, inputShow: boolean, minElement, index?: number) {
    event.stopPropagation();

    this.removeInputRangeStatus();

    item['minInputShow'] = inputShow;

    if (undefined !== index) {
      this.changeDetect.detectChanges();
      this.availableRange(item, index);
      $(minElement).trigger('focus');
    }
  }

  public showMaxInputColorRange(item, inputShow: boolean, maxElement, index?: number) {
    event.stopPropagation();

    this.removeInputRangeStatus();

    item['maxInputShow'] = inputShow;

    if (undefined !== index) {
      this.changeDetect.detectChanges();
      this.availableRange(item, index);
      $(maxElement).trigger('focus');
    }
  }

  private setHextoRgb(color: string): string {
    color = _.cloneDeep(color.replace('#', ''));

    const rColor = parseInt(color.substring(0, 2), 16);
    const gColor = parseInt(color.substring(2, 4), 16);
    const bColor = parseInt(color.substring(4), 16);

    return 'rgb(' + rColor + ',' + gColor + ',' + bColor + ')';
  }

  private setUserCodes(color: Object): Object {
    if (
      (!_.eq(ChartColorType.SERIES, this.color.type) && !_.eq(ChartType.GAUGE, this.uiOption.type)) ||
      (_.eq(ChartType.GAUGE, this.uiOption.type) && !_.eq(ChartColorType.DIMENSION, this.color.type)) ||
      !(<UIChartColorBySeries>this.color).mapping
    )
      return;

    const colorList = ChartColorList[(<UIChartColorBySeries>this.color).schema];
    (<UIChartColorBySeries>this.color).mappingArray.forEach((item, index) => {
      if (_.eq(colorList[index], item['color'])) {
        const changedColorList = ChartColorList[color['colorNum']];

        (<UIChartColorBySeries>this.color).mapping[item['alias']] = changedColorList[index];
        (<UIChartColorBySeries>this.color).mappingArray[index]['color'] = changedColorList[index];
      }
    });

    return <UIChartColorBySeries>this.color;
  }

  private setMapping(): UIChartColor {
    let chartColorList = [];

    if (-1 == (<UIChartColorBySeries>this.color)?.schema?.indexOf('VC')) {
      chartColorList = ChartColorList[(<UIChartColorBySeries>this.color).schema];
    } else {
      chartColorList = <any>ChartColorList['SC1'];
    }

    if (!(<UIChartColorBySeries>this.color).mapping)
      (<UIChartColorBySeries>this.color).mapping = {} as { alias: string; color: string };

    if ((<UIChartColorBySeries>this.color).schema) {
      let colorChangedFl = false;

      for (const key in (<UIChartColorBySeries>this.color).mapping) {
        const index = _.findIndex(this.uiOption.fieldMeasureList, { alias: key });

        if (-1 == index || colorChangedFl) {
          delete (<UIChartColorBySeries>this.color).mapping[key];
          colorChangedFl = true;
        }
      }

      this.uiOption.fieldMeasureList.forEach((item, index) => {
        if ((<UIChartColorBySeries>this.color).schema && !(<UIChartColorBySeries>this.color).mapping[item.alias]) {
          (<UIChartColorBySeries>this.color).mapping[item.alias] = chartColorList[index];
        }
      });

      (<UIChartColorBySeries>this.color).mappingArray = [];

      Object.keys((<UIChartColorBySeries>this.color).mapping).forEach((key) => {
        (<UIChartColorBySeries>this.color).mappingArray.push({
          alias: key,
          color: (<UIChartColorBySeries>this.color).mapping[key],
        });
      });
    }

    return this.color;
  }

  private setFieldList(shelveFieldType?: ShelveFieldType): string[] {
    const getShelveReturnString = (shelve: any, typeList: ShelveFieldType[]): string[] => {
      const resultList: string[] = [];
      _.forEach(shelve, (value, key) => {
        shelve[key].map((item) => {
          if (_.eq(item.type, typeList[0]) || _.eq(item.type, typeList[1])) {
            resultList.push(item.name);
          }
        });
      });
      return resultList;
    };

    return getShelveReturnString(this.pivot, [
      shelveFieldType ?
        shelveFieldType :
        ShelveFieldType.DIMENSION,
      ShelveFieldType.TIMESTAMP
    ]);
  }

  private setRangeViewByDecimal(ranges: ColorRange[]) {
    if (!ranges || 0 == ranges.length) return;

    const decimal = this.uiOption.valueFormat != null ? this.uiOption.valueFormat.decimal : 0;

    const commaUseFl = this.uiOption.valueFormat != null ? this.uiOption.valueFormat.useThousandsSep : false;

    const returnList: any = _.cloneDeep(ranges);

    for (const item of returnList) {
      item['fixMax'] =
        null == item.fixMax ? null : <any>FormatOptionConverter.getDecimalValue(item.fixMax, decimal, commaUseFl);
      item['fixMin'] =
        null == item.fixMin ? null : <any>FormatOptionConverter.getDecimalValue(item.fixMin, decimal, commaUseFl);
      item['gt'] = null == item.gt ? null : <any>FormatOptionConverter.getDecimalValue(item.gt, decimal, commaUseFl);
      item['lte'] = null == item.lte ? null : <any>FormatOptionConverter.getDecimalValue(item.lte, decimal, commaUseFl);
    }

    return returnList;
  }

  private parseStrFloat(range: any): any {
    range.fixMax = null == range.fixMax ? null : FormatOptionConverter.getNumberValue(range.fixMax);
    range.fixMin = null == range.fixMin ? null : FormatOptionConverter.getNumberValue(range.fixMin);
    range.gt = null == range.gt ? null : FormatOptionConverter.getNumberValue(range.gt);
    range.lte = null == range.lte ? null : FormatOptionConverter.getNumberValue(range.lte);
    return range;
  }

  private checkMinZero(minValue: number, elseValue: number) {
    let returnValue: number = elseValue;

    switch (this.uiOption.type) {
      case ChartType.BAR:
      case ChartType.LINE:
      case ChartType.SCATTER:
      case ChartType.BOXPLOT:
      case ChartType.COMBINE:
        if (minValue >= 0) returnValue = 0;
    }

    return returnValue;
  }

  private isNumberRegex(value: any): boolean {
    if (value.indexOf(',') != -1) {
      value = value.replace(/,/g, '');
    }
    return !isNaN(Number(value)); // Number(value) !== NaN;
  }

  private removeInputRangeStatus() {
    _.each(this.rangesViewList, (rangeVal) => {
      if (rangeVal['minInputShow']) delete rangeVal['minInputShow'];
      if (rangeVal['maxInputShow']) delete rangeVal['maxInputShow'];
    });
    if (!_.isUndefined(this.color['ranges']) && this.color['ranges'].length > 0) {
      _.each(this.color['ranges'], (uiRangeVal) => {
        if (uiRangeVal['minInputShow']) delete uiRangeVal['minInputShow'];
        if (uiRangeVal['maxInputShow']) delete uiRangeVal['maxInputShow'];
      });
    }
  }
}
